import FirebaseAnalytics from '@shamrock-core/common/analytics/firebase-analytics';
import {
  AnalyticsEvents,
  AnalyticsModules,
  AnalyticsPages,
  Application,
  ApplicationDocument,
  BankingApplicant,
  DeviceInfo,
  formApplicationSections,
} from 'client-experience-toolbox';
import { getClosestCityLatLong } from 'services/googleMapsService';
import { shouldSkipSection } from './useFormNetworkCalls';
import alloy from '@alloyidentity/web-sdk';
import Config from 'Config';
import { DOFirebaseEvents } from 'app/types';

export const upsertRestrictedFields = ['irsTaxIdOrEin', 'ssnOrItin'];
export const emailFields = ['email', 'contactEmail'];
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const deepCombine = (mainObj: { [key: string]: any }, partialObj: any) => {
  Object.entries(partialObj).forEach(([partialObjKey, partialObjVal]) => {
    // Handle nested string keys (e.g., { 'address.street': '123 Main St' })
    if (partialObjKey.includes('.') || partialObjKey.includes('[')) {
      const keys = partialObjKey.split('.').flatMap((key) => key.split(/[[\]]/).filter(Boolean));
      const lastKeyIndex = keys.length - 1;
      let currentObj = mainObj;

      keys.forEach((key, index) => {
        if (index === lastKeyIndex) {
          currentObj[key] = partialObjVal;
        } else {
          if (!currentObj[key]) {
            // Check if the next key is an array index
            const nextKey = keys[index + 1];
            currentObj[key] = /^\d+$/.test(nextKey) ? [] : {};
          }
          currentObj = currentObj[key];
        }
      });
    } else if (
      // Handle nested objects (e.g., {'address': { 'street': '123 Main St' }})
      typeof partialObjVal === 'object' &&
      partialObjVal !== null &&
      !Array.isArray(partialObjVal) &&
      partialObjKey in mainObj
    ) {
      mainObj[partialObjKey] = deepCombine(mainObj[partialObjKey], partialObjVal);
    } else {
      mainObj[partialObjKey] = partialObjVal;
    }
  });

  return mainObj;
};

export const formatDate = (dateString: string | number) => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
};
export const sendDOFirebaseEvent = async (
  event: AnalyticsEvents | DOFirebaseEvents,
  page: AnalyticsPages,
  params: Record<string, string>,
) => {
  FirebaseAnalytics.sendEvent(event, AnalyticsModules.DIGITAL_ONBOARDING, page, params);
};

const nonNullObjectHelper = (obj: Record<string, unknown>) => {
  if (typeof obj === 'object' && obj !== null) {
    return preparePatchPayload(obj, true);
  }
  return obj;
};

export const preparePatchPayload = (obj: Record<string, unknown>, skipArrayConversion = false) => {
  const result: Record<string, unknown> = {};

  Object.entries(obj).forEach(([key, value]) => {
    if (upsertRestrictedFields.some((field) => key.includes(field))) return;

    if (Array.isArray(value)) {
      if (skipArrayConversion) {
        const filteredArray = value.map((item) => nonNullObjectHelper(item));
        result[key] = filteredArray;
      } else {
        // Convert an array to an object with index as key
        const arrayObject: Record<string, unknown> = {};
        value.forEach((item, index) => {
          arrayObject[index] = nonNullObjectHelper(item);
        });
        result[key] = arrayObject;
      }
    } else if (typeof value === 'object' && value !== null) {
      result[key] = preparePatchPayload(value as Record<string, unknown>);
    } else {
      if (emailFields.includes(key) && typeof value === 'string') {
        value = value.toLowerCase();
      }
      result[key] = value;
    }
  });

  return result;
};

export const prepareSecretPatchPayloadData = (applicationData: Record<string, any>) => {
  const companyIrsTaxIdOrEin = applicationData?.company?.irsTaxIdOrEin;
  const controlProng = applicationData?.controlProng;
  const applicants = applicationData?.applicants ?? [];

  const result: {
    companyIrsTaxIdOrEin?: string;
    applicants: Array<{ key: string; ssnOrItin: string }>;
  } = {
    applicants: [],
  };

  if (companyIrsTaxIdOrEin) result.companyIrsTaxIdOrEin = companyIrsTaxIdOrEin;

  applicants.forEach((applicant: BankingApplicant) => {
    const { applicantId, key, ssnOrItin } = applicant;
    if (ssnOrItin) result.applicants.push({ key: applicantId ?? key!, ssnOrItin });
  });

  if (controlProng?.key === 'controlProng' && controlProng.ssnOrItin) {
    result.applicants.push({ key: 'controlProng', ssnOrItin: controlProng.ssnOrItin });
  }

  return result;
};

export const getApplicationDataValue = (obj: any, stringKey: string) => {
  const keySegments = stringKey.split('.');
  let value = obj;
  for (let i = 1; typeof value === 'object' && i < keySegments.length; i++) {
    const key = keySegments[i];
    const arrayMatch = key.match(/^([^[]+)\[(\d+)\]$/);

    if (arrayMatch) {
      const arrKey = arrayMatch[1];
      const arrIndex = parseInt(arrayMatch[2], 10);
      value = value?.[arrKey]?.[arrIndex];
    } else {
      value = value?.[key];
    }
  }
  return value;
};

export const getFirstIncompleteSection = (app: Partial<Application>) => {
  const sections = app.sectionsCompletion ?? {};
  const applicationType = app.type;
  if (applicationType) {
    const applicationSections = formApplicationSections(applicationType);
    let currentPageIndex = applicationSections.length - 1;

    for (let i = 0; i < applicationSections.length; i++) {
      const sectionName = applicationSections[i] as keyof ApplicationDocument['sectionsCompletion'];

      if (shouldSkipSection(app, sectionName as string)) {
        continue;
      }

      if (!sections[sectionName]?.completedAt) {
        currentPageIndex = i;
        break;
      }
    }

    return currentPageIndex <= 0 ? 1 : currentPageIndex + 1;
  }
  return 1;
};

export const getDeviceInfo = (): DeviceInfo => {
  const userAgent = navigator.userAgent;
  const appVersion = '1.0.0'; // Same as register web app version
  const os = userAgent.includes('Windows') ? 'Windows' : 'Mac OS';
  const deviceLanguage = navigator.language;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return {
    type: 'web',
    appVersion,
    os,
    deviceLanguage,
    timezone,
  };
};

export const getLocation = async (): Promise<{
  city: string;
  state: string;
  countryCode: string;
  lat: string;
  long: string;
}> => {
  if (!navigator.geolocation) {
    console.error('Geolocation is not supported by this browser.');
    return { city: '', state: '', countryCode: '', lat: '', long: '' };
  }

  try {
    if ((await navigator.permissions.query({ name: 'geolocation' })).state === 'denied') {
      throw new Error('Geolocation permission denied');
    }

    const position = await new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject);
    });

    const { latitude, longitude } = position.coords;
    const locationData = await getClosestCityLatLong(latitude, longitude);
    return !locationData
      ? { city: '', state: '', countryCode: '', lat: '', long: '' }
      : {
          city: locationData.city,
          state: locationData.state,
          countryCode: locationData.countryCode,
          lat: latitude.toString(),
          long: longitude.toString(),
        };
  } catch (error) {
    console.error('Error getting location:', error);
    return { city: '', state: '', countryCode: '', lat: '', long: '' };
  }
};

export const initAlloy = async (journeyApplicationToken?: string) => {
  return await alloy.init({
    key: Config.alloyKey,
    production: Config.alloyProductionFlag,
    journeyToken: Config.alloyJourneyToken,
    journeyApplicationToken,
  });
};
